<script>
import { formatPhoneNumber } from '@utils/phone-format'
import { permissionsService } from '@utils/permissions'
import { isProd } from '@utils/environment'

export default {
  props: {
    sims: {
      type: Array,
      required: true,
    },
    groups: {
      type: Array,
      required: true,
    },
    hardwares: {
      type: Array,
      default: () => {
        return []
      },
      required: false,
    },
  },
  data() {
    return {
      isVanishedAdmin: permissionsService.isVanishedAdmin(),
      searchText: '',
      searchInput: null,
      searchedColumn: '',
      formatPhoneNumber: formatPhoneNumber,
      columns: [
        {
          title: 'IMSI',
          dataIndex: 'imsi',
          key: 'imsi',
          scopedSlots: {
            filterDropdown: 'filterDropdown',
            filterIcon: 'filterIcon',
            customRender: 'customRender',
          },
          onFilter: (value, record) =>
            record.imsi.toString().toLowerCase().includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus()
              }, 0)
            }
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.imsi.localeCompare(b.imsi),
        },
        {
          title: 'MTN #',
          dataIndex: 'mtn',
          key: 'mtn',
          scopedSlots: {
            filterDropdown: 'filterDropdown',
            filterIcon: 'filterIcon',
            customRender: 'customRender',
          },
          onFilter: (value, record) =>
            record.mtn.toString().toLowerCase().includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus()
              }, 0)
            }
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.mtn.localeCompare(b.mtn),
        },
        {
          title: 'Slot',
          dataIndex: 'simSlot',
          key: 'simSlot',
          sorter: (a, b) => a.simSlot - b.simSlot,
          scopedSlots: {
            filterDropdown: 'filterDropdown',
            filterIcon: 'filterIcon',
            customRender: 'customRender',
          },
          onFilter: (value, record) =>
            record.simSlot
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus()
              }, 0)
            }
          },
          defaultSortOrder: 'ascend',
        },
        {
          title: 'Hardware',
          dataIndex: 'hardwareId',
          key: 'hardwareId',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.hardwareId.localeCompare(b.hardwareId),
          filters: isProd()
            ? [
                {
                  text: 'MD',
                  value: 'md',
                },
                {
                  text: 'CA',
                  value: 'ca',
                },
                {
                  text: 'LA',
                  value: 'la',
                },
                {
                  text: 'VA',
                  value: 'va',
                },
              ]
            : [
                {
                  text: 'LA',
                  value: 'la',
                },
                {
                  text: 'VA',
                  value: 'va',
                },
              ],
          // specify the condition of filtering result
          // here is that finding the name started with `value`
          onFilter: (value, record) => record.hardwareId.indexOf(value) === 0,
        },
        {
          title: 'Group(s)',
          dataIndex: 'groupId',
          key: 'groupId',
          scopedSlots: {
            customRender: 'customRender',
          },
        },
        {
          title: 'Organization',
          dataIndex: 'orgId',
          key: 'orgId',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.orgId.localeCompare(b.orgId),
        },
        {
          title: 'Actions',
          key: 'actions',
          scopedSlots: {
            customRender: 'customRender',
          },
        },
      ],
    }
  },
  methods: {
    getGroupColor(id) {
      if (id && this.groups) {
        const group = this?.groups.filter((g) => g.sk === id)[0]
        const color = group?.meta?.color
        if (color) {
          return color
        } else {
          return 'pink'
        }
      } else {
        return 'pink'
      }
    },
    sortGroup(record) {
      if (record?.groupId?.length > 0) {
        const x = JSON.parse(JSON.stringify(record))
        return x.groupId.sort()
      }
      return []
    },
    selectRow(record) {
      const simId = record.pk.replace('SIM#', '')
      this.$router.push({
        name: 'sim-profile',
        params: { simId },
      })
    },
    onDelete(record) {
      this.$emit('deleteSim', record)
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm()
      this.searchText = selectedKeys[0]
      this.searchedColumn = dataIndex
    },

    handleReset(clearFilters) {
      clearFilters()
      this.searchText = ''
    },
  },
}
</script>

<template>
  <a-table :data-source="sims" :columns="columns">
    <div
      slot="filterDropdown"
      slot-scope="{
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        column,
      }"
      style="padding: 8px"
    >
      <a-input
        v-ant-ref="(c) => (searchInput = c)"
        :placeholder="`Search ${column.dataIndex}`"
        :value="selectedKeys[0]"
        style="display: block; width: 188px; margin-bottom: 8px"
        @change="(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])"
        @pressEnter="
          () => handleSearch(selectedKeys, confirm, column.dataIndex)
        "
      />
      <a-button
        type="primary"
        icon="search"
        size="small"
        style="width: 90px; margin-right: 8px"
        @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
      >
        Search
      </a-button>
      <a-button
        size="small"
        style="width: 90px"
        @click="() => handleReset(clearFilters)"
      >
        Reset
      </a-button>
    </div>
    <a-icon
      slot="filterIcon"
      slot-scope="filtered"
      type="search"
      :style="{ color: filtered ? '#108ee9' : undefined }"
    />
    <template slot="customRender" slot-scope="text, record, index, column">
      <span v-if="searchText && searchedColumn === column.dataIndex">
        <template
          v-for="(fragment, i) in text
            .toString()
            .split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
        >
          <mark
            v-if="fragment.toLowerCase() === searchText.toLowerCase()"
            :key="i"
            class="highlight"
            >{{ fragment }}</mark
          >
          <template v-else>{{ fragment }}</template>
        </template>
      </span>
      <template v-else>
        <div v-if="column.key === 'imsi'">
          <a @click="() => selectRow(record, column)">{{ text }}</a>
        </div>
        <div v-else-if="column.key === 'groupId'">
          <div v-if="record.groupId.length">
            <a-tag
              v-for="(id, i) in sortGroup(record)"
              :key="i"
              style="text-transform: uppercase"
              :color="getGroupColor(id)"
              >{{ id.replace('GROUP#', '') }}
            </a-tag>
          </div>
        </div>
        <div v-else-if="column.key === 'mtn'">
          {{ formatPhoneNumber(text) }}
        </div>
        <div v-else-if="column.key === 'orgId'">
          <a-tag color="green">
            {{ text.toUpperCase() }}
          </a-tag>
        </div>
        <div v-else-if="column.key === 'actions'">
          <ul class="actions">
            <li>
              <a
                href="javascript:;"
                title="Edit SIM"
                @click="() => selectRow(record, column)"
              >
                <a-button type="primary" icon="edit" size="small"></a-button>
              </a>
            </li>
            <li v-if="isVanishedAdmin">
              <a-popconfirm
                title="Sure to delete?"
                @confirm="() => onDelete(record)"
              >
                <a href="javascript:;" title="Edit SIM">
                  <a-button type="danger" icon="delete" size="small"></a-button>
                </a>
              </a-popconfirm>
            </li>
          </ul>
        </div>
        <div v-else-if="column.key === 'hardwareId'">
          {{ text.toUpperCase() }}
        </div>
        <div v-else>
          {{ text }}
        </div>
      </template>
    </template>
  </a-table>
</template>

<style scoped>
.highlight {
  padding: 0;
  background-color: rgb(255, 192, 105);
}
</style>
